import React, { useEffect, useState } from 'react'
import {
  ProcedureModel,
  ProcedureDetailModel
} from '../../../pages/comunicationCenter/tabs/procedures/comunicationProcedures.model'
import { UserPermission } from '@globaleris/ui.library'
import { hasPermission } from '../../../helpers/permissionsHelper'
import { Resources } from '../../../model/RBAC/constants'
import {
  comunicationsProceduresDetailMapper,
  comunicationsProceduresListItemsMapper
} from '../../../pages/comunicationCenter/tabs/procedures/comunicationProcedures.mapper'
import {
  fetchProcedureItem,
  fetchProceduresListItemsByClient,
  fetchProceduresListItemsByDates
} from '../../../pages/comunicationCenter/tabs/procedures/comunicationProdecures.service'
import { getActiveBalanceZone, getActiveCompany } from '../../../redux'
import { useHttpError } from '../../../hooks'
import { completeDataTableActions } from './proceduresTable.service'
import { DatePeriod } from '../../../model'

interface UseProcedures {
  procedures: ProcedureModel[] | undefined
  procedure: ProcedureDetailModel | undefined
  setProcedure: React.Dispatch<React.SetStateAction<number | undefined>>
  isFetchingProcedures: boolean
  isFetchingProcedure: boolean
  refreshProcedures: () => Promise<void>
}

interface UseProceduresProps {
  clientID?: number
  datePeriod?: DatePeriod
}

export default function useProcedures({ clientID, datePeriod }: UseProceduresProps): UseProcedures {
  const [isFetchingProcedures, setIsFetchingProcedures] = useState<boolean>(false)
  const [isFetchingProcedure, setIsFetchingProcedure] = useState<boolean>(false)
  const [procedures, setProcedures] = useState<ProcedureModel[] | undefined>(undefined)
  const [idProcedureSelected, setProcedure] = useState<number | undefined>(undefined)
  const [procedure, setProcedureSelected] = useState<ProcedureDetailModel | undefined>(undefined)
  const activeBalanceZone = getActiveBalanceZone()?.id
  const activeCompany = getActiveCompany()?.id

  const { showError, clearError, errorProps } = useHttpError()

  const getProcedures = async () => {
    let result: ProcedureModel[] | undefined
    if (clientID) {
      result = await fetchProceduresbyClient(clientID)
    } else if (datePeriod) {
      result = await fetchProceduresbyDates(datePeriod)
    }
    setProcedures(result)
  }

  const fetchProceduresbyClient = async (
    clientID: number
  ): Promise<ProcedureModel[] | undefined> => {
    setIsFetchingProcedures(true)
    setProcedures(undefined)

    return await fetchProceduresListItemsByClient(clientID).then(response => {
      setIsFetchingProcedures(false)
      if (response.success) {
        clearError()
        if (response.data !== undefined) {
          return comunicationsProceduresListItemsMapper(response.data).map(
            (item: ProcedureModel) => {
              return completeDataTableActions(item)
            }
          )
        } else {
          return []
        }
      } else {
        response.error &&
          showError({
            errorCode: response.error.code,
            onRefresh: async () => {
              setProcedures(await fetchProceduresbyClient(clientID))
            }
          })
        return []
      }
    })
  }

  const fetchProceduresbyDates = async (
    dates: DatePeriod
  ): Promise<ProcedureModel[] | undefined> => {
    setIsFetchingProcedures(true)
    setProcedures(undefined)

    return await fetchProceduresListItemsByDates(dates).then(response => {
      setIsFetchingProcedures(false)
      if (response.success) {
        clearError()
        if (response.data !== undefined) {
          return comunicationsProceduresListItemsMapper(response.data).map(
            (item: ProcedureModel) => {
              return completeDataTableActions(item)
            }
          )
        } else {
          return []
        }
      } else {
        response.error &&
          showError({
            errorCode: response.error.code,
            onRefresh: async () => {
              setProcedures(await fetchProceduresbyDates(dates))
            }
          })
        return []
      }
    })
  }

  const fetchProcedure = async (
    idProcedure: number | undefined
  ): Promise<ProcedureModel | undefined> => {
    setIsFetchingProcedure(true)
    setProcedureSelected(undefined)

    hasPermission(Resources.ComunicationsCenterProcedures, [
      UserPermission.INFO,
      UserPermission.SEND,
      UserPermission.EDIT,
      UserPermission.READ_ONLY
    ])

    return await fetchProcedureItem(idProcedure).then(response => {
      setIsFetchingProcedure(false)

      if (response.success) {
        clearError()
        if (response.data !== undefined) {
          return comunicationsProceduresDetailMapper(response.data)
        } else {
          return undefined
        }
      } else {
        return undefined
      }
    })
  }

  useEffect(() => {
    // onNotificationChange(undefined)

    getProcedures()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientID, datePeriod, activeBalanceZone, activeCompany])

  useEffect(() => {
    const getProcedure = async () => {
      setProcedureSelected(await fetchProcedure(idProcedureSelected))
    }

    if (idProcedureSelected) {
      getProcedure()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idProcedureSelected])

  return {
    procedures,
    procedure,
    setProcedure,
    isFetchingProcedures,
    isFetchingProcedure,
    refreshProcedures: getProcedures
  }
}
